/**
 * useMealPlan - Comprehensive hook for managing meal plans
 *
 * This hook combines all meal plan functionality for both PRO editor
 * and consumer dashboard contexts. It orchestrates meal management,
 * nutrition analysis, permissions, and save state.
 *
 * Features:
 * - Complete meal plan state management
 * - Integrates meal operations, nutrition, and modals
 * - Handles both virtual (PRO) and saved (consumer) plans
 * - Permission-aware operations
 * - Auto-save and dirty state tracking
 * - Undo/redo support
 */
import indexBy from 'lodash.indexby';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { usePlanAnalysis } from './usePlanAnalysis';
import { useMealManagement } from './useMealManagement';
import { usePlanDates } from './usePlanDates';
import { useModalManager } from './useModalManager';
import { useVirtualPlan } from './useVirtualPlan';
import { useSchedulePlan } from './useSchedulePlan';
import { usePlanLoading } from './usePlanLoading';
import { usePlanSaving } from './usePlanSaving';
import { getAssetsForMeals } from '@utils/Meals';
import confirm from '@utils/Confirm';

/**
 * Configuration for meal plan management
 * @typedef {Object} MealPlanConfig
 * @property {Object} initialPlan - Initial plan data
 * @property {Object} profile - User/patient profile
 * @property {Object} permissions - Plan permissions (PRO only)
 * @property {boolean} autoSave - Enable auto-save
 * @property {number} autoSaveDelay - Auto-save delay in ms
 * @property {boolean} enableUndo - Enable undo/redo
 * @property {number} maxUndoSteps - Maximum undo history
 * @property {Object} router - Router instance for modals
 * @property {Function} showUpgradeForm - Function to show upgrade/subscription form
 * @property {Function} showLoginForm - Function to show login form
 * @property {boolean} isCordova - Whether running in Cordova app
 */

/**
 * Main meal plan management hook
 *
 * @param {MealPlanConfig} config - Configuration object
 * @returns {Object} Complete meal plan management interface
 */
export const useMealPlan = (config = {}) => {
    const {
        planId = null,           // Plan UUID to load
        initialPlan = null,      // Initial plan data (for testing)
        profile = {},
        user = null,             // User object
        autoSave = false,
        autoSaveDelay = 3000,
        enableUndo = false,
        maxUndoSteps = 20,
        router = null,
        envelope = null,         // Nutrient targets/envelope
        showUpgradeForm = null,
        showLoginForm = null,
        isCordova = false,
        onPlanUuidChanged = null, // Callback when plan UUID changes
    } = config;

    // Virtual plan management - moved inside the hook
    const virtualPlanHandler = useVirtualPlan(planId);

    // Consolidated plan loading - handles all loading concerns (no currentPlan passed to avoid loops)
    const planLoading = usePlanLoading(planId, virtualPlanHandler, initialPlan);

    // Create stable plan object - use loaded plan or empty plan as fallback
    const plan = useMemo(() => {
        return planLoading.plan || createEmptyPlan();
    }, [planLoading.plan, profile.uuid, profile.portion]);

    // Create stable references to prevent infinite loops
    const stablePlanLoadingSetPlan = useCallback((newPlan) => {
        planLoading.setPlan(newPlan);
    }, [planLoading.setPlan]);

    // Memoize onPlanSaved callback to prevent recreation on every render
    const onPlanSavedCallback = useCallback((newPlan) => {
        // Update plan state when save is successful
        const oldUuid = plan?.uuid;
        const newUuid = newPlan.uuid;

        stablePlanLoadingSetPlan(newPlan);

        if (oldUuid !== newUuid) {
            onPlanUuidChanged(newUuid, oldUuid);
        }
    }, [plan?.uuid, stablePlanLoadingSetPlan, onPlanUuidChanged]);

    // Consolidated plan saving - handles all saving concerns
    const planSaving = usePlanSaving({
        plan,
        virtualPlanHandler,
        permissions: planLoading.permissions,
        profile,
        user,
        isDietitianRecommended: planLoading.isDietitianRecommended,
        autoSave,
        autoSaveDelay,
        onPlanSaved: onPlanSavedCallback,
        onPlanUuidChanged
    });

    // Create stable references for frequently used methods
    const stableMarkDirty = useCallback(() => {
        planSaving.saveState.markDirty();
    }, [planSaving.saveState.markDirty]);

    const stableAutoSaveToLocal = useCallback((planData) => {
        planSaving.autoSaveToLocal(planData);
    }, [planSaving.autoSaveToLocal]);

    // Handle marking dirty when plan loads from local storage
    useEffect(() => {
        if (planLoading.shouldMarkDirty && !planLoading.loading) {
            stableMarkDirty();
        }
    }, [planLoading.shouldMarkDirty, planLoading.loading, stableMarkDirty]);

    // Create empty plan structure
    function createEmptyPlan() {
        return {
            uuid: uuid(),
            name: 'New Meal Plan',
            items: [],
            participants: [{ uuid: profile.uuid, portion: profile.portion || 1 }],
            type: 'plan'
        };
    }

    // Undo/redo state
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    // Max offset state (independent of meals after initial load)
    const [maxOffset, setMaxOffset] = useState(0);

    // Nutrition recalculation trigger - increments to force nutrition re-analysis
    const [nutritionKey, setNutritionKey] = useState(0);

    // Date management
    const planDates = usePlanDates({
        meals: plan.items || [],
        defaultDuration: 7
    });

    /**
     * Get meal types to render
     *
     * Always show meal type rows regardless of meal content
     */
    const mealTypes = useMemo(() => {
        // Check if plan itself has meal_types
        if (plan.meal_types && Array.isArray(plan.meal_types)) {

            if (profile.preferences?.meal_types && Array.isArray(profile.preferences.meal_types)) {
                const profileMealTypeIndex = indexBy(profile.preferences.meal_types, 'name')

                // Inherited additional properties from the profile's meal types too
                return plan.meal_types.map(mt => ({
                    ...mt,
                    ...profileMealTypeIndex[mt.name]
                }));
            }

            return plan.meal_types;
        }

        // If we have a profile with meal types, use those as the source of truth
        if (profile?.preferences?.meal_types) {
            return profile.preferences.meal_types;
        }

        // LEGACY: Derive meal types from plan items when no profile meal types available
        const planMealTypes = [...new Set((plan.items || []).map(item => item.meal))];

        // Helper function to get default meal times
        const getDefaultMealTime = (mealName, timeType) => {
            const defaults = {
                'Breakfast': { begin: '07:00', end: '09:00' },
                'Lunch': { begin: '12:00', end: '14:00' },
                'Dinner': { begin: '18:00', end: '20:00' },
                'Snack': { begin: '15:00', end: '16:00' }
            };
            return defaults[mealName]?.[timeType] || '12:00';
        };

        // Helper to convert time string to minutes from midnight
        const minutesFromMidnight = (timeString) => {
            const [hours, minutes] = timeString.split(':').map(Number);
            return hours * 60 + minutes;
        };

        // If plan is empty, provide default meal types to ensure grid always shows
        if (planMealTypes.length === 0) {
            const defaultMealTypes = ['Breakfast', 'Lunch', 'Dinner'];
            return defaultMealTypes.map(mealName => ({
                name: mealName,
                begin: minutesFromMidnight(getDefaultMealTime(mealName, 'begin')),
                end: minutesFromMidnight(getDefaultMealTime(mealName, 'end'))
            }));
        }

        // FALLBACK: Create basic meal type objects from plan for legacy support
        return planMealTypes.map(mealName => ({
            name: mealName,
            begin: minutesFromMidnight(getDefaultMealTime(mealName, 'begin')),
            end: minutesFromMidnight(getDefaultMealTime(mealName, 'end'))
        }));
    }, [plan, profile]);

    const nutrition = usePlanAnalysis({
        meals: plan.items || [],
        assets: plan.assets || null, // Virtual plans won't have assets initially
        profile,
        plan,
        mealTypes,
        nutritionKey // Force recalculation when this changes
    });

    // Modal management - pure state, no business logic
    const modalManager = useModalManager({
        permissions: planLoading.permissions
    });

    const mealManagement = useMealManagement({
        meals: plan.items || [],
        onMealsChange: (newMeals) => updatePlan({ items: newMeals }),
        profile,
        assets: nutrition.assets,
        currentDate: planDates.startDate,
        trackHistory: enableUndo,
        modalManager,
        plan
    });

    // Schedule plan functionality
    const schedulePlan = useSchedulePlan({
        plan,
        profile,
        user,
        router,
        modalManager,
        showUpgradeForm,
        showLoginForm,
        isCordova,
        synced: !planSaving.isSaving && planSaving.saveState.isOnline,
        recipes: nutrition.assets.recipes,
        savePlan: planSaving.savePlan, // Use the save function from the saving hook
        onComplete: (planMarker) => {
            console.log('Plan scheduled successfully:', planMarker);
        }
    });

    /**
     * Update plan with undo support
     */
    const updatePlan = useCallback((updates) => {
        // Save to undo stack if enabled
        if (enableUndo) {
            setUndoStack(prev => {
                const newStack = [...prev, plan];
                return newStack.slice(-maxUndoSteps); // Limit stack size
            });
            setRedoStack([]); // Clear redo on new change
        }

        const newPlan = {
            ...plan,
            ...updates
        };

        // Dirty State Handling for Soft Delete:
        // Only mark dirty if this is NOT a soft-delete operation
        // Soft delete = meals marked as deleted:true (undoable)
        // Final cleanup = meals actually removed (should mark dirty)
        let shouldMarkDirty = true;

        if (updates.items) {
            const oldMeals = plan.items || [];
            const newMeals = updates.items;

            // Check if this is a soft delete: same count, some meals gained deleted:true
            const isSoftDelete = (
                oldMeals.length === newMeals.length &&
                newMeals.some(meal => meal.deleted) &&
                !oldMeals.every((oldMeal, idx) => newMeals[idx]?.deleted === oldMeal.deleted)
            );

            // Check if this is a restore: meals losing deleted:true flag
            const isRestore = (
                oldMeals.length === newMeals.length &&
                oldMeals.some(meal => meal.deleted) &&
                !newMeals.some(meal => meal.deleted)
            );

            // Don't mark dirty for soft deletes or restores (both are undoable)
            if (isSoftDelete || isRestore) {
                shouldMarkDirty = false;
            }
        }

        if (shouldMarkDirty) {
            stableMarkDirty();
        }

        // Auto-save to local storage
        stableAutoSaveToLocal(newPlan);

        // Force nutrition recalculation if meals changed
        if (updates.items) {
            setNutritionKey(prev => prev + 1);
        }

        // Update the plan in the loading hook
        stablePlanLoadingSetPlan(newPlan);
    }, [enableUndo, maxUndoSteps, plan, stableMarkDirty, stableAutoSaveToLocal, stablePlanLoadingSetPlan]);

    /**
     * Undo last change
     */
    const undo = useCallback(() => {
        if (undoStack.length === 0) return;

        const previousPlan = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));
        setRedoStack(prev => [...prev, plan]);
        stablePlanLoadingSetPlan(previousPlan);
        stableMarkDirty();
    }, [undoStack, plan, stablePlanLoadingSetPlan, stableMarkDirty]);

    /**
     * Redo last undone change
     */
    const redo = useCallback(() => {
        if (redoStack.length === 0) return;

        const nextPlan = redoStack[redoStack.length - 1];
        setRedoStack(prev => prev.slice(0, -1));
        setUndoStack(prev => [...prev, plan]);
        stablePlanLoadingSetPlan(nextPlan);
        stableMarkDirty();
    }, [redoStack, plan, stablePlanLoadingSetPlan, stableMarkDirty]);

    /**
     * Clear all meals
     */
    const clearPlan = useCallback(async () => {
        const shouldClear = await confirm('Are you sure you want to clear all meals?');
        if (shouldClear) {
            updatePlan({ items: [] });
        }
    }, [updatePlan]);

    /**
     * Add a day to the meal plan (increment maxOffset)
     */
    const addDay = useCallback(() => {
        if (maxOffset >= 6) return false; // Max 7 days (0-6 offset)

        setMaxOffset(prev => prev + 1);
        stableMarkDirty();

        return true;
    }, [maxOffset, stableMarkDirty]);


    /**
     * Initialize maxOffset from meals when plan loads (only once per plan)
     */
    useEffect(() => {
        const computedMaxOffset = plan.items.length === 0
            ? 0
            : Math.max(...plan.items.map(item => item.offset || 0));

        setMaxOffset(computedMaxOffset);
    }, [plan.uuid]); // Only reset when plan UUID changes (new plan loaded)

    /**
     * Get plan summary
     */
    const summary = useMemo(() => {
        const activeMeals = plan.items.filter(m => !m.deleted);
        const mealsByType = {};

        activeMeals.forEach(meal => {
            const type = meal.meal || 'Other';
            mealsByType[type] = (mealsByType[type] || 0) + 1;
        });

        return {
            totalMeals: activeMeals.length,
            mealsByType,
            totalCalories: nutrition.analysis?.nutrients?.['208'] || 0,
            totalProtein: nutrition.analysis?.nutrients?.['203'] || 0,
            participantCount: plan.participants?.length || 1,
            hasLeftovers: activeMeals.some(m => m.meal_type === 'leftover'),
            meetsRequirements: false
        };
    }, [plan, nutrition.analysis?.nutrients]);


    // Groceries are now calculated directly by the nutrition hook
    const groceries = nutrition.groceries;


    return {
        // Plan data
        plan,
        summary,
        mealTypes,
        maxOffset,

        // Plan operations
        updatePlan,
        savePlan: planSaving.savePlan,
        duplicatePlan: planSaving.duplicatePlan,
        clearPlan,
        addDay,

        // Meal operations
        mealManager: mealManagement,
        ...mealManagement,

        // Nutrition data
        nutrition,

        // Assets (recipes, foods, etc.)
        assets: nutrition.assets,

        // Grocery calculations
        groceries,

        // Modal management
        modalManager,

        // Date management
        planDates,

        // Save state (includes justSaved)
        saveState: planSaving.saveState,

        // Success message state (from saveState for convenience)
        justSaved: planSaving.saveState.justSaved,

        // Duplicate state
        isDuplicating: planSaving.isDuplicating,

        // Loading states
        loading: planLoading.loading,
        error: planLoading.error,

        // Undo/redo
        undo: enableUndo ? undo : null,
        redo: enableUndo ? redo : null,
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,

        // Permissions
        permissions: planLoading.permissions,
        canEdit: planLoading.permissions.canWrite !== false,
        canPublish: planLoading.permissions.canPublish === true,

        // Virtual plan handler
        virtualPlanHandler,

        // Schedule plan functionality
        schedulePlan
    };
};
